/**
 * Badge catalogue and league labels. The server alone awards badges (check_achievements()
 * in Postgres); this file only describes them for display and mirrors the level labels of
 * public.level_label() so the profile can show the learner's own level offline.
 */

export type AchievementIcon = 'flame' | 'cards' | 'book' | 'books' | 'star' | 'moon' | 'sun' | 'trophy' | 'bolt';

export interface AchievementInfo {
  code: string;
  title: string;
  /** How to earn it, shown on a locked badge. */
  howTo: string;
  icon: AchievementIcon;
}

export const ACHIEVEMENTS: readonly AchievementInfo[] = [
  { code: 'first_lesson', title: 'Первый урок', howTo: 'Пройдите все 4 главы одного урока', icon: 'book' },
  { code: 'perfect_chapter', title: 'Без ошибок', howTo: 'Пройдите главу без единой ошибки', icon: 'star' },
  { code: 'streak_3', title: '3 дня подряд', howTo: 'Занимайтесь 3 дня подряд', icon: 'flame' },
  { code: 'streak_7', title: 'Неделя', howTo: 'Занимайтесь 7 дней подряд', icon: 'flame' },
  { code: 'streak_30', title: 'Месяц', howTo: 'Занимайтесь 30 дней подряд', icon: 'flame' },
  { code: 'streak_100', title: '100 дней', howTo: 'Занимайтесь 100 дней подряд', icon: 'flame' },
  { code: 'words_50', title: '50 слов', howTo: 'Повторите 50 разных слов', icon: 'cards' },
  { code: 'words_200', title: '200 слов', howTo: 'Повторите 200 разных слов', icon: 'cards' },
  { code: 'words_1000', title: '1000 слов', howTo: 'Повторите 1000 разных слов', icon: 'cards' },
  { code: 'lessons_10', title: '10 уроков', howTo: 'Пройдите 10 уроков', icon: 'books' },
  { code: 'first_book', title: 'Целая книга', howTo: 'Пройдите все уроки одной книги', icon: 'books' },
  { code: 'xp_1000', title: '1000 XP', howTo: 'Наберите 1000 XP за всё время', icon: 'bolt' },
  { code: 'night_owl', title: 'Ночная сова', howTo: 'Позанимайтесь уроком после 23:00', icon: 'moon' },
  { code: 'early_bird', title: 'Ранняя пташка', howTo: 'Позанимайтесь уроком с 4:00 до 7:00', icon: 'sun' },
  { code: 'league_win', title: 'Чемпион недели', howTo: 'Займите 1-е место в лиге за неделю', icon: 'trophy' },
];

const BY_CODE = new Map(ACHIEVEMENTS.map((a) => [a.code, a]));

export function achievementInfo(code: string): AchievementInfo {
  return BY_CODE.get(code) ?? { code, title: code, howTo: '', icon: 'star' };
}

export interface BadgeView extends AchievementInfo {
  earned: boolean;
  /** League wins can repeat: how many weeks were won. */
  count: number;
}

/** Every badge in catalogue order, marked earned or locked. Unknown codes are ignored. */
export function badgeGrid(earned: readonly { code: string }[]): BadgeView[] {
  const counts = new Map<string, number>();
  for (const e of earned) counts.set(e.code, (counts.get(e.code) ?? 0) + 1);
  return ACHIEVEMENTS.map((a) => ({ ...a, earned: (counts.get(a.code) ?? 0) > 0, count: counts.get(a.code) ?? 0 }));
}

/** Codes present in `after` that were not in `before` (for the unlock animation). */
export function newlyEarned(before: readonly { code: string; period: string }[], after: readonly { code: string; period: string }[]): string[] {
  const seen = new Set(before.map((a) => `${a.code}|${a.period}`));
  return after.filter((a) => !seen.has(`${a.code}|${a.period}`)).map((a) => a.code);
}

/** Mirrors public.level_label(): the level a book teaches. */
export function levelLabel(bookId: string, courseTitle: string): string {
  if (bookId.startsWith('zh-hsk5')) return 'HSK 5';
  if (bookId.startsWith('zh-hsk4')) return 'HSK 4';
  if (bookId.startsWith('en-evolve-1')) return 'A1';
  if (bookId.startsWith('en-evolve-2')) return 'A2';
  if (bookId.startsWith('en-evolve-3')) return 'B1';
  return courseTitle;
}

/** Small language marks for the league rows. */
export const LANG_MARK: Readonly<Record<string, string>> = { zh: '中', en: 'EN', tr: 'TR' };
export const LANG_NAME_RU: Readonly<Record<string, string>> = { zh: 'Китайский', en: 'Английский', tr: 'Турецкий' };
